import { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { cardSurface, reveal } from "features/onboarding/common/onboardingUi";
import { selectWorkspace } from "@shared/slices/common.slice";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import DcaDemo from "features/onboarding/guided/DcaDemo";
import useSceneWalkthrough from "features/onboarding/guided/walkthrough/useSceneWalkthrough";
import { ONBOARDING_LEARN_METHOD_ID } from "testIds";
import WalkthroughPanel from "features/onboarding/guided/walkthrough/WalkthroughPanel";

type Method = "dca" | "signals";

const LiveMethodExample = ({ method }: { method: Method }) => {
  const workspace = useSelector(selectWorkspace);
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const [open, setOpen] = useState(false);
  if (workspace !== WorkspaceType.USER) return null;
  return (
    <Details onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary data-testid={ONBOARDING_LEARN_METHOD_ID}>
        {t("revision.see_method", { method: t(`intent.${method}.name`) })}
      </summary>
      {open && (
        <Example>{method === "dca" ? <DcaDemo /> : <SignalsExample />}</Example>
      )}
    </Details>
  );
};

export default LiveMethodExample;

const SignalsExample = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const walk = useSceneWalkthrough("signals", 4);
  const { stage, chartRef, advance, playing, togglePlayback } = walk;
  const actionLabel =
    stage === 4
      ? t("intent.replay")
      : stage === 0
      ? t("intent.signals.action")
      : t(`revision.signals_next${stage as 1 | 2 | 3}`);
  return (
    <WalkthroughPanel
      intent="signals"
      stage={stage}
      total={4}
      label={t(`revision.signals_visual${stage as 0 | 1 | 2 | 3 | 4}`)}
      caption={
        stage === 0
          ? t("intent.waiting")
          : walk.steps
          ? walk.steps[stage - 1].description!
          : t(`revision.signals_caption${stage as 1 | 2 | 3 | 4}`)
      }
      playing={playing}
      {...walk.panel}
      togglePlayback={togglePlayback}
      advance={advance}
      actionLabel={actionLabel}
      chartRef={chartRef}
    />
  );
};

const Details = styled.details`
  ${cardSurface};
  ${reveal(3)};
  width: 100%;
  margin: 0 0 24px;
  summary {
    padding: 14px 20px;
    font-weight: 600;
    cursor: pointer;
    color: ${(p) => p.theme.colors.primaryBlue};
  }
  summary:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 2px;
    border-radius: ${(p) => p.theme.radii.card}px;
  }
`;
const Example = styled.div`
  max-width: 620px;
  margin: 0 auto;
  padding: 0 16px 16px;
`;
